import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import terrapinTradeLogo from '/TTLogo6.png';
import VideoModal from './shared/VideoModal';
import { DashboardIcon, DollarIcon, TrendingIcon, ChartBarIcon, AnalyticsIcon, SettingsIcon, ActivityIcon, DatabaseIcon, ReceiptIcon } from './shared/Icons';
import './Navigation.css';

interface NavigationProps {
  isCollapsed?: boolean;
  onNavigate?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ isCollapsed = false, onNavigate }) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string>('/cooter-capital-where-dreams-are-made.mp4');

  const pickRandomAnimation = async (): Promise<string> => {
    const manifestUrl = '/terrapin-trade/animations/index.json';
    const fallback = '/cooter-capital-where-dreams-are-made.mp4';
    try {
      const res = await fetch(manifestUrl, { cache: 'no-cache' });
      if (!res.ok) return fallback;
      const list = await res.json();
      if (!Array.isArray(list) || list.length === 0) return fallback;
      const chosen = list[Math.floor(Math.random() * list.length)];
      return `/terrapin-trade/animations/${String(chosen).replace(/^\/+/, '')}`;
    } catch (err) {
      // If anything goes wrong, return fallback
      // eslint-disable-next-line no-console
      console.error('Failed to load animations manifest', err);
      return fallback;
    }
  };

  const handleLogoClick = () => {
    // don't return a promise from the click handler directly
    void (async () => {
      const src = await pickRandomAnimation();
      setVideoSrc(src);
      setIsVideoModalOpen(true);
    })();
  };

  const handleCloseVideo = () => {
    setIsVideoModalOpen(false);
  };

  return (
    <>
      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={handleCloseVideo}
        videoSrc={videoSrc}
        title="Terrapin Trade - Where Dreams Are Made"
      />

      {/* Overlay backdrop for mobile */}
      <div
        className={`sidebar-overlay ${!isCollapsed ? 'active' : ''}`}
        onClick={onNavigate}
      />

      <nav className={`sidebar-navigation ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          {!isCollapsed && (
            <>
              <div className="logo-container">
                <div className="logo-text-above">TERRAPIN</div>
                <div className="logo-text-below">TRADE</div>
                <img
                  src={terrapinTradeLogo}
                  alt="Terrapin Trade"
                  className="sidebar-logo clickable-logo"
                  onClick={handleLogoClick}
                  title="Click to watch Terrapin Trade - Where Dreams Are Made"
                />
              </div>
            </>
          )}
          {isCollapsed && (
            <img
              src={terrapinTradeLogo}
              alt="Terrapin Trade"
              className="sidebar-logo-small clickable-logo"
              onClick={handleLogoClick}
              title="Click to watch Terrapin Trade - Where Dreams Are Made"
            />
          )}
        </div>

        <ul className="sidebar-menu">
          <li>
            <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'} title="Portfolio Dashboard" onClick={onNavigate}>
              <DashboardIcon />
              <span>Portfolio Dashboard</span>
            </NavLink>
          </li>
          <li>
            <NavLink to="/profit" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'} title="Profit Monitor" onClick={onNavigate}>
              <DollarIcon />
              <span>Profit Monitor</span>
            </NavLink>
          </li>
          <li>
            <NavLink to="/market-overview" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'} title="Market Overview" onClick={onNavigate}>
              <ActivityIcon />
              <span>Market Overview</span>
            </NavLink>
          </li>
          <li>
            <NavLink to="/trade-groups" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'} title="Trade Groups" onClick={onNavigate}>
              <TrendingIcon />
              <span>Trade Groups</span>
            </NavLink>
          </li>
          <li>
            <NavLink to="/capital" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'} title="Capital Deployment" onClick={onNavigate}>
              <ChartBarIcon />
              <span>Capital Deployment</span>
            </NavLink>
          </li>
          <li>
            <NavLink to="/analytics" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'} title="Performance Analytics" onClick={onNavigate}>
              <AnalyticsIcon />
              <span>Performance Analytics</span>
            </NavLink>
          </li>
          <li>
            <NavLink to="/trading-config" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'} title="Trading Configuration" onClick={onNavigate}>
              <DatabaseIcon />
              <span>Trading Config</span>
            </NavLink>
          </li>
          <li>
            <NavLink to="/tax-reporting" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'} title="Tax Reporting" onClick={onNavigate}>
              <ReceiptIcon />
              <span>Tax Reporting</span>
            </NavLink>
          </li>
          <li>
            <NavLink to="/settings" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'} title="Settings" onClick={onNavigate}>
              <SettingsIcon />
              <span>Settings</span>
            </NavLink>
          </li>
        </ul>
      </nav>
    </>
  );
};

export default Navigation;
